import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import AlumnosTable from './AlumnosTable';
import PlanEstudiosTable from './PlanEstudiosTable';
import {
  BookOpen, User, Calendar, ArrowLeft, FileText,
  MessageSquare, Users, Home, Clock
} from 'lucide-react';

function PublicCatedraView() {
  const { id } = useParams();
  const [catedra, setCatedra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedPlanData, setProcessedPlanData] = useState([]);
  const [activeTab, setActiveTab] = useState('plan');

  const stripHtmlTags = (htmlString) => {
    if (!htmlString) return '';
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || '';
  };

  useEffect(() => {
    const getCatedra = async () => {
      try {
        const { data } = await api.fetchPublicCatedraById(id);
        setCatedra(data);
      } catch (err) {
        setError(err);
        console.error('Error fetching public catedra by ID:', err);
      } finally {
        setLoading(false);
      }
    };

    getCatedra();
  }, [id]);

  const toArray = (val) => {
    if (Array.isArray(val)) return val.map(v => (v == null ? '' : String(v))).filter(Boolean);
    if (val == null) return [];
    if (typeof val === 'string') return val.split('\n').map(s => s.trim()).filter(Boolean);
    return [String(val)].map(s => s.trim()).filter(Boolean);
  };

  useEffect(() => {
    if (catedra && catedra.planDeClases) {
      const transformedPlan = catedra.planDeClases.map(unidad => {
        const contenidoRaw = unidad.contenido ?? unidad.unidad;
        const estrategiasRaw = unidad.estrategias ?? unidad.estrategiasMetodologicas;
        const evaluacionRaw = unidad.evaluacion ?? unidad.mediosVerificacionEvaluacion ?? '';

        return {
          periodo: unidad.periodo,
          unidad: contenidoRaw ?? '',
          contenidos: toArray(contenidoRaw),
          capacidades: unidad.capacidades ?? '',
          horasTeoricas: unidad.horasTeoricas ?? '',
          horasPracticas: unidad.horasPracticas ?? '',
          estrategias: toArray(estrategiasRaw),
          evaluacion: Array.isArray(evaluacionRaw) ? evaluacionRaw.join('\n') : String(evaluacionRaw ?? ''),
        };
      });
      setProcessedPlanData(transformedPlan);
    }
  }, [catedra]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg font-medium">Cargando información de la cátedra...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-red-500/40 p-8 text-center">
          <p className="text-red-400 text-lg font-semibold">Error al cargar la cátedra: {error.message}</p>
          <Link to="/public/catedras" className="mt-4 inline-block px-5 py-2 bg-slate-800 text-white rounded-xl">
            Volver a Cátedras
          </Link>
        </div>
      </div>
    );
  }

  if (!catedra) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <p className="text-slate-300 text-lg">Cátedra no encontrada.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-10 px-4 sm:px-6 lg:px-8 text-white">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header de la Cátedra */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
          <Link
            to="/public/catedras"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={18} />
            Volver al listado de cátedras
          </Link>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-900/30 flex-shrink-0">
                <BookOpen size={36} />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2">
                  {catedra.nombre}
                </h1>
                <div className="flex flex-wrap gap-4 text-sm text-slate-300">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={16} className="text-emerald-400" />
                    Año {catedra.anio}
                  </span>
                  {catedra.docente && (
                    <span className="flex items-center gap-1.5">
                      <User size={16} className="text-blue-400" />
                      Docente: <strong>{catedra.docente.nombre} {catedra.docente.apellido}</strong>
                    </span>
                  )}
                  {catedra.institucion && (
                    <span className="flex items-center gap-1.5">
                      <Home size={16} className="text-purple-400" />
                      {catedra.institucion}
                    </span>
                  )}
                  {catedra.dias && (
                    <span className="flex items-center gap-1.5">
                      <Clock size={16} className="text-amber-400" />
                      {catedra.dias} {catedra.turno ? `(${catedra.turno})` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navegación por Pestañas de la Vista Pública */}
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-slate-800">
            <button
              onClick={() => setActiveTab('plan')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'plan'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-slate-800/70 text-slate-300 hover:bg-slate-800'
              }`}
            >
              <BookOpen size={17} />
              Plan de Estudios ({processedPlanData.length})
            </button>
            <button
              onClick={() => setActiveTab('actividades')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'actividades'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-slate-800/70 text-slate-300 hover:bg-slate-800'
              }`}
            >
              <MessageSquare size={17} />
              Actividades y Tareas ({(catedra.actividades?.length || 0) + (catedra.tareas?.length || 0)})
            </button>
            <button
              onClick={() => setActiveTab('alumnos')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'alumnos'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-slate-800/70 text-slate-300 hover:bg-slate-800'
              }`}
            >
              <Users size={17} />
              Alumnos y Calificaciones ({catedra.alumnos?.length || 0})
            </button>
          </div>
        </div>

        {/* Sección: Plan de Clases */}
        {activeTab === 'plan' && (
          <section>
            <PlanEstudiosTable planData={processedPlanData} loading={loading} />
          </section>
        )}

        {/* Sección: Actividades y Tareas */}
        {activeTab === 'actividades' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Actividades / Publicaciones */}
            <section className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-2xl font-bold mb-4 text-purple-300 flex items-center gap-2">
                <MessageSquare size={22} />
                Actividades de la Cátedra
              </h3>
              {catedra.actividades && catedra.actividades.length > 0 ? (
                <div className="space-y-3">
                  {catedra.actividades.map((actividad) => (
                    <div
                      key={actividad.id}
                      className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 space-y-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-bold text-white">{actividad.titulo}</h4>
                        {actividad.created_at && (
                          <span className="text-xs text-slate-400">
                            {new Date(actividad.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-300">
                        {stripHtmlTags(actividad.contenido || actividad.descripcion)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No hay actividades registradas para esta cátedra.</p>
              )}
            </section>

            {/* Tareas */}
            <section className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-2xl font-bold mb-4 text-amber-300 flex items-center gap-2">
                <FileText size={22} />
                Tareas Asignadas
              </h3>
              {catedra.tareas && catedra.tareas.length > 0 ? (
                <div className="space-y-3">
                  {catedra.tareas.map((tarea) => (
                    <div
                      key={tarea.id}
                      className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 space-y-1.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-bold text-white">{tarea.titulo}</h4>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          {tarea.puntos_posibles} pts
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">
                        {stripHtmlTags(tarea.descripcion)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No hay tareas asignadas para esta cátedra.</p>
              )}
            </section>
          </div>
        )}

        {/* Sección: Alumnos, Asistencia y Calificación Final */}
        {activeTab === 'alumnos' && (
          <section>
            <AlumnosTable catedraId={id} />
          </section>
        )}
      </div>
    </div>
  );
}

export default PublicCatedraView;
